import { readItemUuidSlot } from '../compile/inventory-raw-readers.js';
import { itemsCol } from '../../shared/array-types.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;

/** Persisted override, else template size. Unsized WORLD props block at Medium; compiled instance defaults are ignored. */
export function inventoryAuthoredSizeId(row: Row, definition: Row | null = null): string | null {
  const inventory = record(row.inventory) ?? row;
  const override = readItemUuidSlot(scalar(inventory.inventory_size));
  const catalogSize = record(definition?.item_layer_types)?.size ?? definition?.size;
  const authored = (override || readItemUuidSlot(scalar(catalogSize))).toUpperCase();
  const itemType = String(itemsCol(row, 'item_type') ?? itemsCol(definition, 'item_type') ?? '');
  return authored || (WORLD_ITEM_TYPES.has(itemType) ? 'DC23AA88-CB3D-EA4E-F6F7-6389ABC4F9DC' : null);
}

/** Compilers attach encounter links at the row root; lean rows may retain them in the inventory bucket. */
export function inventoryEncounterLink(row: Row): boolean {
  const enabled = (value: unknown): boolean => {
    const v = scalar(value);
    return v !== undefined && v !== null && v !== '' && v !== '0' && v !== 0 && v !== false;
  };
  return enabled(row.inventory_encounter_link) || enabled(record(row.inventory)?.inventory_encounter_link);
}

/** Passage names inherit from the catalog when the inventory has no name. */
export function inventoryPropName(row: Row, definition: Row | null = null): string {
  for (const value of [record(row.inventory)?.inventory_name, row.inventory_name,
    record(row.items)?.item_name, record(definition?.items)?.item_name, row.item_name, definition?.item_name]) {
    const name = String(scalar(value) ?? '').trim();
    if (name) return name;
  }
  return '';
}

/**
 * Compiled item_layer_types has no authored/default provenance: PHP build_inventory_arr and the browser's
 * inventory-final-economy both materialize off_hand there, and catalog normalization can retain compiled buckets.
 * Until that provenance is held, use ONLY items columns (the floor/push/router contract), never a layer reader.
 */
export function inventoryEquipmentSlot(row: Row, definition: Row | null = null): unknown {
  const value = record(row.items)?.equipment_slot ?? record(definition?.items)?.equipment_slot;
  return Array.isArray(value) ? value[0] : value;
}

export interface InventoryFixtureFlags {
  immovable: boolean;
  integratedObject: boolean;
}

/** Fixed-world layers use the scene's boolean contract; sparse omissions are false. */
export function inventoryFixtureFlags(value: (key: string) => unknown): InventoryFixtureFlags {
  const enabled = (key: string): boolean => {
    const raw = value(key);
    return [true, 1, '1'].includes((Array.isArray(raw) ? raw[0] : raw) as true | 1 | '1');
  };
  return { immovable: enabled('immovable'), integratedObject: enabled('integrated_object') };
}

/** Authored placement only: the mesh supplies scenery; rooms retain physical authority. */
export function inventorySceneryShell(row: Row): boolean {
  const inventory = record(row.inventory) ?? row;
  let generation = inventory.inventory_generation;
  if (typeof generation === 'string') { try { generation = JSON.parse(generation); } catch { return false; } }
  return record(generation)?.sceneryShell === true;
}

export interface SolidInputs extends Partial<InventoryFixtureFlags> {
  sceneryShell?: boolean;
  private: boolean; itemType: string; name: string; equipmentSlot: unknown; quantity: number;
  encounterLink: boolean; wallRun: boolean; corpseLinked: boolean;
  /** Selected runtime world look, even when its bounds or multi-part assembly cannot be measured. */
  meshDrawn: boolean;
}

const LOOSE_ITEM_TYPES = new Set(['weapon', 'natural_weapon', 'armor', 'shield', 'ammunition', 'consumable',
  'potion', 'scroll', 'wand', 'ring', 'currency', 'gem', 'trade_good', 'tool', 'instrument', 'clothing', 'jewelry', 'book', 'component',
  'gemstone', 'ammo', 'spell_material_component', 'elixir', 'poison', 'coating', 'food', 'drink', 'reagent', 'herb',
  'monster_bit', 'ingot', 'ore', 'enchanting_dust', 'key', 'keyring', 'barding', 'tack_and_harness']);

export const WORLD_ITEM_TYPES = new Set(['furniture', 'building', 'tree', 'door', 'wall', 'window', 'floor',
  'war_machine', 'vehicle_land', 'vehicle_water', 'vehicle_air', 'plant', 'food_plant', 'herbal_plant', 'ore_vein', 'digsite']);

/** Scene and Janus obstacle membership. Fixed fixtures stay physical even when their template has a wear slot. */
export function inventorySolid(input: SolidInputs): boolean {
  if (input.private || input.corpseLinked || input.wallRun || input.sceneryShell === true || input.itemType === 'map_marker' || input.itemType === 'roof') return false;
  if (input.immovable === true || input.integratedObject === true || input.itemType === 'door' || input.encounterLink) return true;
  if (input.equipmentSlot || input.quantity > 1 || LOOSE_ITEM_TYPES.has(input.itemType)) return false;
  // Owner 2026-10-18: mesh decides; served Torch/candle/lock/plant definitions all gain default Tiny,
  // so size cannot prove physicality. WORLD standees (95–100% sized/fixed in the raw catalog) stay physical;
  // other standees are loot. Passage names only affect navigation AFTER this membership decision.
  return input.meshDrawn || WORLD_ITEM_TYPES.has(input.itemType);
}
